/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import { Button, InlineNotification, PageNotification, Typography2 } from '@coursera/cds-core';
import { ComposeIcon } from '@coursera/cds-icons';

import InlineSubmissionTool from 'bundles/assess-common/components/InlineSubmissionTool';
import useBoxAnnotatorProfile from 'bundles/assess-common/hooks/useBoxAnnotatorProfile';
import useCreateBoxDocumentAnnotationSession from 'bundles/assess-common/hooks/useCreateBoxDocumentAnnotationSession';
import { CMLOrHTML } from 'bundles/cml';
import type { FormPartsValidationStatus } from 'bundles/compound-assessments/components/form-parts/lib/checkResponsesInvalid';
import { GraderTypes, typeNames } from 'bundles/compound-assessments/constants';
import type {
  BoxViewDocumentAnnotationPrompt,
  BoxViewDocumentAnnotationResponse,
} from 'bundles/compound-assessments/types/FormParts';

import _t from 'i18n!nls/compound-assessments';

export type Props = {
  prompt: BoxViewDocumentAnnotationPrompt;
  responseId: string;
  courseId: string;
  itemId: string;
  userId: number;
  isReadOnly: boolean;
  isGraderView: boolean;
  onChangeResponse: (response: BoxViewDocumentAnnotationResponse) => void;
  submissionId?: string;
  isExpanded?: boolean;
  isQuickGraderContext?: boolean;
};

export const checkInvalid = (): FormPartsValidationStatus | null => {
  return null;
};

const styles = {
  notification: css`
    margin-bottom: var(--cds-spacing-100);
  `,
  prompt: css`
    margin-bottom: var(--cds-spacing-100);
  `,
  toolEmbed: css`
    width: 100%;
  `,
};

const BoxViewDocumentAnnotation = ({
  prompt,
  responseId,
  courseId,
  itemId,
  userId,
  isReadOnly,
  isGraderView,
  onChangeResponse,
  submissionId,
  isExpanded = false,
  isQuickGraderContext,
}: Props) => {
  const [showInlineSubmissionTool, setShowInlineSubmissionTool] = useState(isExpanded);
  const {
    annotatorProfileId,
    loading: annotatorProfileLoading,
    error: annotatorProfileError,
  } = useBoxAnnotatorProfile(isReadOnly);
  const {
    session,
    createBoxSessionForView,
    createBoxSessionForViewStatus,
    createBoxSessionForGrading,
    createBoxSessionForGradingStatus,
  } = useCreateBoxDocumentAnnotationSession();

  const markAnnotationAsCompletedIfNecessary = () => {
    if (showInlineSubmissionTool && !isReadOnly) {
      onChangeResponse({
        typeName: typeNames.REVIEW_RESPONSE,
        definition: {
          graderType: GraderTypes.Human,
          reviewPart: {
            typeName: 'boxViewDocumentAnnotation',
            definition: {
              annotationComplete: true,
              fileId: '',
            },
          },
        },
      });
    }
  };

  const handleInlineSubmissionToolOnClick = async () => {
    // the button is disabled if the courseId, itemId, or submissionId is not set
    // this is to help with linting below
    if (!courseId || !itemId || !submissionId) {
      return;
    }

    if (isReadOnly) {
      await createBoxSessionForView({
        variables: {
          input: {
            courseId,
            itemId,
            responseId,
            submissionId,
          },
        },
      });
    } else {
      await createBoxSessionForGrading({
        variables: {
          body: {},
          itemId,
          courseId,
          reviewResponseId: responseId,
          userId: userId.toString(),
          action: 'createForGrading',
          annotatorProfileId: annotatorProfileId ? `&annotatorProfileId=${annotatorProfileId}` : '',
        },
      });
    }

    setShowInlineSubmissionTool(true);
    markAnnotationAsCompletedIfNecessary();
  };

  useEffect(() => {
    // The quick grader component controls this widget's visibility via the isExpanded prop.
    // Quick grader uses its own button to trigger the tool.
    // When isExpanded changes, ensure the widget displays correctly in following contexts:
    // - Remove the widget when quick grader switches rubrics to show file preview instead.
    //  - Alternatively, handle sequential rendering of inline feedback rubrics.
    // - Re-render the widget when switching between view-only and grader modes when grade
    // visibility is changed.
    if (isQuickGraderContext) {
      if (isExpanded) {
        handleInlineSubmissionToolOnClick();
      } else {
        setShowInlineSubmissionTool(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isQuickGraderContext, isExpanded]);

  // if the user swaps read only mode, we need to reset the widget, so force the user to click
  // the entry button again
  // this is only relevant for the quick grader context when swapping grade visibility
  useEffect(() => {
    setShowInlineSubmissionTool(false);
  }, [isReadOnly]);

  if (!prompt) {
    return null;
  }

  const editText = isReadOnly ? _t('View inline feedback') : _t('Give inline feedback');

  return (
    <div className="rc-BoxViewDocumentAnnotation">
      <Typography2
        className="rc-BoxViewDocumentAnnotation__prompt"
        component="div"
        variant="bodyPrimary"
        css={styles.prompt}
      >
        <CMLOrHTML value={prompt.promptContent} />
      </Typography2>
      {(createBoxSessionForViewStatus.error || createBoxSessionForGradingStatus.error || annotatorProfileError) && (
        <InlineNotification severity="error" title={_t('Error')} css={styles.notification}>
          {_t('Failed to retrieve file. Please try again later.')}
        </InlineNotification>
      )}
      {!isQuickGraderContext && isGraderView && isReadOnly && (
        <PageNotification severity="information" css={styles.notification}>
          {_t('Grade visibility must be hidden to give inline feedback.')}
        </PageNotification>
      )}
      {showInlineSubmissionTool && session && (
        <div css={styles.toolEmbed}>
          <InlineSubmissionTool
            source={session.source}
            sandbox={session.sandbox || ''}
            iframeTitle={session.iframeTitle || ''}
            rpcActionTypes={session.rpcActionTypes}
            expiresAt={Math.floor(new Date(session.expiresAt || '').getTime() / 1000)}
            sessionId={session.id}
            reviewResponseId={responseId}
          />
        </div>
      )}
      {!showInlineSubmissionTool && !isQuickGraderContext && (
        <div>
          <Button
            data-testid="inline-feedback-entry-button"
            size="medium"
            variant="ghost"
            iconPosition="before"
            icon={<ComposeIcon />}
            onClick={handleInlineSubmissionToolOnClick}
            loading={
              createBoxSessionForViewStatus.loading ||
              createBoxSessionForGradingStatus.loading ||
              annotatorProfileLoading
            }
            disabled={!courseId || !itemId || !submissionId}
          >
            {editText}
          </Button>
        </div>
      )}
    </div>
  );
};

export default BoxViewDocumentAnnotation;
